import React, { useState, useEffect, useCallback } from 'react';
import debounce from 'lodash/debounce';

import { Icon } from '../../entries/components';
import OTPLogin from './OTPLogin';
import { makeTranslator } from '../../utilities/translations';
import LoadingSpinner from 'sos-common/js/components/LoadingSpinner/LoadingSpinner';

import { CaseListProps } from './CaseList.types';
import {
    FileSessionVM,
    FileSessionType,
    FilterOption,
    ArendeType
} from '../FileAPIClient/Api.types';
import { formatDiarienummer } from '../CaseList/Support';

import './caselist.scss';

import { FileAPIClient } from '../FileAPIClient/FileApiClient';
import { ResourceItem } from 'sos-common/js/commonTypes';

export const CaseList: React.FC<CaseListProps> = (props) => {
    const Translate = makeTranslator(props.translation);
    const filterValues = [
        { Text: Translate('filterAlla'), Value: FilterOption.alla },
        { Text: Translate('filterUpp'), Value: FilterOption.upp },
        { Text: Translate('filterNed'), Value: FilterOption.ned },
    ];

    const [fileAPIClient, setFileAPIClient] = useState<FileAPIClient>();
    const [allFileSessions, setAllFileSessions] = useState<FileSessionVM[]>([]);
    const [filteredFileSessions, setFilteredFileSessions] = useState<FileSessionVM[]>([]);

    const [selectedFilter, setSelectedFilter] = useState(FilterOption.alla);
    const [searchTerm, setSearchTerm] = useState("");

    const [isLoading, setIsLoading] = useState(false);

    const [isAuthenticated, setIsAuthenticated] = useState(false);

    useEffect(() => {
        initComponent();
    }, []);

    useEffect(() => {
        if (isAuthenticated) {
            populateData();
        }
    }, [isAuthenticated]);

    const initComponent = async () => {
        setIsLoading(true);
        try {
            let apiClient = new FileAPIClient(() => { });
            setFileAPIClient(apiClient);
            setIsAuthenticated(await apiClient.isAuthenticated());
        }
        catch (error: Error | any) {
            setIsAuthenticated(false);
            console.log(error.message);
        }
        finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        const lowerSearchTerm = searchTerm?.toLowerCase() ?? '';

        const isMatchFilter = (x: FileSessionVM) => {
            switch (selectedFilter) {
                case FilterOption.ned:
                    return x.transferType === FileSessionType.Outbound;
                case FilterOption.upp:
                    return x.transferType === FileSessionType.Inbound;
                default:
                    return true;
            }
        };

        const isMatchSearch = (x: FileSessionVM) => {
            return lowerSearchTerm === '' || x.arende.title.toLowerCase().includes(lowerSearchTerm) ||
                x.arende.diarienummer.toLowerCase().includes(lowerSearchTerm);
        };

        const filteredCases = allFileSessions.filter(x => isMatchFilter(x) && isMatchSearch(x));

        setFilteredFileSessions(filteredCases);
    }, [selectedFilter, searchTerm, allFileSessions]);

    const populateData = async () => {
        setIsLoading(true);
        if (!fileAPIClient) {
            setIsLoading(false);
            return;
        }
        try {
            await fileAPIClient.isAuthenticated();
            if (!isAuthenticated) {
                throw new Error(Translate('/error/accesscheck'));
            }
            var response = await fileAPIClient.getFileSessionsForUser()
            if (!response.success || !response.data) {
                if (response.code === 403) {
                    setIsAuthenticated(false);
                    return;
                }
                else {
                    throw new Error(Translate('/error/somethingwentwrong'));
                }
            }

            setAllFileSessions(response.data);
            setFilteredFileSessions(response.data);
        }
        catch (error: Error | any) {
            console.error('Misslyckades att hämta data. ', error.message);
        }
        finally {
            setIsLoading(false);
        }
    };

    const handleSearch = useCallback(debounce((value: string) => {
        setSearchTerm(value);
    }, 300), []);

    const handleFilterChange = (value: FilterOption) => {
        setSelectedFilter(value);
    };

    const getFileHandlingPageLinkTranslation = (fileTransferType: FileSessionType) => {
        return fileTransferType === FileSessionType.Inbound
            ? Translate("linkUpp")
            : Translate("linkNed");
    }

    const getArendetypTranslation = (arendeType: ArendeType) => {
        switch (arendeType) {
            case ArendeType.Forskning:
                return Translate('forskning');
            case ArendeType.Statistik:
                return Translate('statistik');
            case ArendeType.Kvalitetsregister:
                return Translate('kvalitet');
            default:
                throw new Error(Translate('/error/nolocalizationstring'));
        }
    };

    const getInfoTextTranslation = (fileTransferType: FileSessionType) => {
        return fileTransferType === FileSessionType.Inbound
            ? Translate("infoUpp")
            : Translate("infoNed");
    }

    const getFormlistClassName = (fileTransferType: FileSessionType) => {
        return fileTransferType === FileSessionType.Inbound
            ? 'formlist-item case-list__item case-list__item--upload'
            : 'formlist-item case-list__item case-list__item--download';
    }

    const getFileHandlingPageLink = (fileSession: FileSessionVM): string => {
        return fileSession.transferType === FileSessionType.Inbound
            ? `${props.pageLinkUpload}?fileSessionId=${fileSession.id.value}`
            : `${props.pageLinkDownload}?fileSessionId=${fileSession.id.value}`;
    }

    const formatInfoDatum = (date: Date | null) => {
        if (!date) {
            return '';
        }
        var dateObj = new Date(date);
        return new Intl.DateTimeFormat('sv-SE', { day: 'numeric', month: 'long' }).format(dateObj);
    };

    const handleLoginSuccess = () => {
        setIsAuthenticated(true);
    }

    const renderCaseList = (items: FileSessionVM[]) => {
        return (
            <div role="list" id="caselist" className="row case-list__spinner-container" aria-busy={isLoading} aria-live="polite">
                {isLoading && (
                    <div className="flex-center">
                        <LoadingSpinner darkColor={true} cssClasses="case-list__spinner" />
                    </div>
                )}

                {!isLoading && items.length === 0 && (
                    <p>{Translate('nohits')}</p>
                )}

                {(!isLoading && items.length > 0) && (
                    items.map((item) => (
                        <div role="listitem" className="col-sm-12" key={`${item.id.value}`}>
                            <div className={getFormlistClassName(item.transferType)}>
                                <div className="formlist-item__primary">
                                    <h2 aria-label={`${Translate('title')}: ${item.arende.title}, ${Translate('diarynumber')}: ${item.arende.diarienummer}`}>
                                        {item.arende.title}: {formatDiarienummer(item.arende.diarienummer)}
                                    </h2>
                                    <div>{Translate('arendetyp')} {getArendetypTranslation(item.arende.arendeType)}</div>
                                    <div>{Translate('info')} {getInfoTextTranslation(item.transferType)}</div>
                                </div>
                                <div className="formlist-item__secondary">
                                    <div className="formlist-item__buttonwrapper">
                                        <div className="formlist-item__linkwrapper">
                                            <a href={getFileHandlingPageLink(item)} aria-label={`${getFileHandlingPageLinkTranslation(item.transferType)} ${Translate('oncasewithtitle')} ${item.arende.title}`}>
                                                {getFileHandlingPageLinkTranslation(item.transferType)}
                                                <Icon iconType="baseline-arrow_back-24px" />
                                            </a>
                                        </div>
                                    </div>
                                    <div className="small-text">
                                        {item.transferType === FileSessionType.Outbound && (
                                            <>
                                                {Translate('infoDatum')} {formatInfoDatum(item.latestDownloadDate)}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>
        );
    };

    const renderLeftSection = () => {
        return (
            <div className="col-md-6 col-sm-12">
                {props.pageHeading && <h1>{props.pageHeading}</h1>}
                {isAuthenticated && props.pageBody && <div dangerouslySetInnerHTML={{ __html: props.pageBody }}></div>}
                {!isAuthenticated && <OTPLogin apiBaseUrl={props.apiBaseUrl} onLoginSuccess={handleLoginSuccess} translations={props.translation} />}
            </div>
        );
    };

    const renderRightSection = () => {
        if (isAuthenticated) {
            return (
                <div className="col-md-6 col-sm-12">
                    <Search
                        placeholder={Translate('placeholder')}
                        onSearchChange={handleSearch}
                        translations={props.translation}
                    />
                </div>
            );
        }
        return <div className="col-md-6 col-sm-12"></div>;
    };

    return (
        <>
            <div className="row">
                {renderLeftSection()}
                {renderRightSection()}
            </div>
            {isAuthenticated && (
                <CaseListFilter
                    filterValues={filterValues}
                    selectedFilter={selectedFilter}
                    onFilterChange={handleFilterChange}
                    translations={props.translation}
                />
            )}
            {isAuthenticated && renderCaseList(filteredFileSessions)}
        </>
    );

}

interface SearchProps {
    placeholder: string;
    onSearchChange: (value: string) => void;
    translations: ResourceItem[];
}

const Search: React.FC<SearchProps> = ({ placeholder, onSearchChange, translations }) => {
    const Translate = makeTranslator(translations);
    return (
        <span className="search">
            <label htmlFor='search-filter' className="search__label sr-only">{Translate('entersearchtext')}</label>
            <input
                aria-label={placeholder}
                type="search"
                id="search-filter"
                aria-controls="caselist"
                placeholder={placeholder}
                className="search__input"
                onChange={(e) => onSearchChange(e.target.value)}
                autoFocus
            />
            <input type="submit" aria-label={Translate('search')} className="search__button search__button--blue" value="" />
        </span>
    );
}

interface CaseListFilterProps {
    filterValues: { Text: string; Value: FilterOption }[];
    selectedFilter: FilterOption;
    onFilterChange: (value: FilterOption) => void;
    translations: ResourceItem[];
}

const CaseListFilter: React.FC<CaseListFilterProps> = ({ filterValues, selectedFilter, onFilterChange, translations }) => {
    const Translate = makeTranslator(translations);
    return (
        <div className="m-t-2 m-b-2 row" role="tablist">
            {' '}
            {filterValues.map((x, index, array) => {
                const isChosen = x.Value === selectedFilter;
                return (
                    <div className="col-md-2 col-4" key={x.Text}>
                        <button
                            onClick={() => onFilterChange(x.Value)}
                            className={`case-list__filter ${x.Value === selectedFilter ? 'case-list__filter--chosen' : 'case-list__filter'}`}
                            role="tab"
                            aria-controls="caselist"
                            aria-selected={isChosen}
                            aria-label={`${x.Text} - ${Translate('filterlistontype')} ${x.Text}`}>
                            {x.Text}
                        </button>
                    </div>
                );
            })}
        </div>
    );
}